import { CompanyLogoPanel } from "@/components/TemplateConfiguration/CompanyLogoPanel.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useSaveTemplateConfiguration } from "@/mutations/useSaveTemplateConfiguration";
import { useFetchAdminConfiguration } from "@/queries/useFetchAdminConfiguration.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { Alert, Box, Button, CircularProgress, Grid2, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import type React from "react";
import { type ReactNode, useEffect, useState } from "react";

const DocumentTemplatesPage = (): ReactNode => {
    const { hasPermission } = useEmployee();
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("/src/assets/CambreLogo.png");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Fetch existing configuration
    const {
        data: { data: adminConfig } = {},
        isLoading,
        error,
        refetch,
    } = useFetchAdminConfiguration();
    const saveTemplateConfiguration = useSaveTemplateConfiguration();

    // Initialize state with fetched data
    useEffect(() => {
        if (adminConfig) {
            // Set logo preview from fetched data - now using logoUrl directly
            if (adminConfig.logoUrl) {
                setLogoPreview(adminConfig.logoUrl);
            } else {
                // Keep default preview if no logo URL
                setLogoPreview("/src/assets/CambreLogo.png");
            }

            setHasUnsavedChanges(false);
        }
    }, [adminConfig]);

    // Track changes for unsaved indicator
    useEffect(() => {
        if (adminConfig) {
            const hasChanges = logoFile !== null;
            setHasUnsavedChanges(hasChanges);
        }
    }, [logoFile, adminConfig]);

    // Cleanup object URL on unmount
    useEffect(() => {
        return () => {
            if (logoPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            // Validate file type
            const validTypes = [
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/gif",
                "image/webp",
                "image/avif",
            ];
            if (!validTypes.includes(file.type)) {
                enqueueSnackbar("Please upload a valid image file (PNG, JPG, GIF, AVIF, or WebP)", {
                    variant: "error",
                });
                return;
            }

            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                enqueueSnackbar("File size must be less than 2MB", { variant: "error" });
                return;
            }

            // Revoke previous object URL if it exists to prevent memory leak
            if (logoPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(logoPreview);
            }

            setLogoFile(file);
            // Use object URL for instant preview instead of readAsDataURL
            const previewUrl = URL.createObjectURL(file);
            setLogoPreview(previewUrl);
            setHasUnsavedChanges(true);
        }
    };

    const handleSaveConfiguration = () => {
        saveTemplateConfiguration.mutate(
            {
                logo: logoFile,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Configuration saved successfully.", { variant: "success" });

                    // Revoke the object URL since we're done with it
                    if (logoPreview?.startsWith("blob:")) {
                        URL.revokeObjectURL(logoPreview);
                    }

                    setHasUnsavedChanges(false);
                    setLogoFile(null); // Reset file input since it's now saved
                    refetch(); // Refresh the data to get the latest
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Configuration save failed.", { variant: "error" });
                    console.error("Save error:", getErrorMessage(error));
                },
            },
        );
    };

    // Loading state
    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "calc(100vh - 64px)",
                    p: 3,
                }}
            >
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading configuration...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    Error loading configuration: {getErrorMessage(error)}
                </Alert>
                <Button variant="outlined" onClick={() => refetch()}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <>
            {!hasPermission("edit:doc-header") && (
                <Box sx={{ p: 3 }}>
                    Restricted Access: You do not have permission to view this page.
                </Box>
            )}
            {hasPermission("edit:doc-header") && (
                <Box
                    sx={{
                        p: 3,
                        backgroundColor: "background.default",
                        minHeight: "calc(100vh - 64px)",
                    }}
                >
                    {/* Page Header */}
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: "2rem",
                                fontWeight: 500,
                                color: "text.primary",
                                mb: 1,
                            }}
                        >
                            Document Templates Configuration
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                lineHeight: 1.5,
                            }}
                        >
                            Configure the header and footer elements that appear on all generated
                            documents. Upload your company logo, manage the list of associates, and
                            set up signature images.
                        </Typography>
                        {adminConfig?.lastModified && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 1,
                                }}
                            >
                                Last modified: {new Date(adminConfig.lastModified).toLocaleString()}{" "}
                                by {adminConfig.modifiedBy}
                            </Typography>
                        )}
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }}>
                        Changes made here will apply to all newly generated documents. Existing
                        documents will not be affected.
                    </Alert>

                    {hasUnsavedChanges && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            You have unsaved changes. Don't forget to save your configuration.
                        </Alert>
                    )}

                    <Grid2 container spacing={3}>
                        {/* Company Logo Section */}
                        <Grid2 size={{ xs: 12 }}>
                            <CompanyLogoPanel
                                logoFile={logoFile}
                                logoPreview={logoPreview}
                                onLogoUpload={handleLogoUpload}
                            />
                        </Grid2>
                    </Grid2>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleSaveConfiguration}
                            disabled={saveTemplateConfiguration.isPending || !hasUnsavedChanges}
                        >
                            {saveTemplateConfiguration.isPending ? (
                                <>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Saving...
                                </>
                            ) : (
                                "Save Configuration"
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default DocumentTemplatesPage;
